import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Head, Loader, STAGES, timeAgo } from "./shared";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, AreaChart, Area, CartesianGrid } from "recharts";
import {
  DollarSign, FolderKanban, FileText, ShoppingBag, UserPlus, Sparkles, CreditCard,
  MessageSquare, ChevronRight, TrendingUp, Activity, ArrowUp, ArrowDown, ArrowRight, Plus, Upload,
  FilePlus, Bot, AlertTriangle, Info, CheckCircle2, Sun, ShieldCheck, Zap, Coins, Timer,
  Star, HeartPulse, Wrench, ChevronDown, Layers, X, Target,
} from "lucide-react";

const QUICK_ACTIONS = [
  { label: "New Client", go: "clients" },
  { label: "New Project", go: "projects" },
  { label: "Proposal", go: "proposals" },
  { label: "AI Studio", go: "ai" },
  { label: "Upload Template", go: "templates" },
];

const ACTIVITY_META = {
  payment: { icon: DollarSign, cls: "bg-emerald-500/15 text-emerald-400", go: "payments" },
  proposal: { icon: FileText, cls: "bg-amber-400/15 text-amber-300", go: "proposals" },
  lead: { icon: UserPlus, cls: "bg-amber-400/15 text-amber-300", go: "requests" },
  order: { icon: ShoppingBag, cls: "bg-emerald-500/15 text-emerald-400", go: "orders" },
  ai: { icon: Bot, cls: "bg-sky-500/15 text-sky-400", go: "ai" },
  review: { icon: Star, cls: "bg-emerald-500/15 text-emerald-400", go: "clients" },
  update: { icon: FolderKanban, cls: "bg-gold/15 text-gold", go: "projects" },
};
const actMeta = (type) => ACTIVITY_META[type] || ACTIVITY_META.update;

const FUNNEL_ICON = { chats: Bot, qualified: CheckCircle2, projects: FolderKanban, proposals: FileText, invoices: CreditCard, customers: DollarSign };

const SEV = {
  red: { dot: "bg-red-500", text: "text-red-300", ring: "border-red-500/30", tile: "bg-red-500/15 text-red-400", icon: AlertTriangle },
  amber: { dot: "bg-amber-400", text: "text-amber-200", ring: "border-amber-400/30", tile: "bg-amber-400/15 text-amber-300", icon: AlertTriangle },
  green: { dot: "bg-emerald-500", text: "text-emerald-300", ring: "border-emerald-500/30", tile: "bg-emerald-500/15 text-emerald-400", icon: CheckCircle2 },
  blue: { dot: "bg-sky-500", text: "text-sky-300", ring: "border-sky-500/30", tile: "bg-sky-500/15 text-sky-400", icon: Info },
};

const Trend = ({ pct, suffix = "" }) => {
  const dir = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  const cls = dir === "up" ? "text-emerald-400" : dir === "down" ? "text-red-400" : "text-gray-400";
  const Icon = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : ArrowRight;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${cls}`}><Icon size={12} />{pct >= 0 ? "+" : ""}{pct}%{suffix}</span>;
};

// Tiny inline-SVG sparkline — a quick visual trend, no heavy chart.
const Sparkline = ({ data, w = 128, h = 32 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1), min = Math.min(...data, 0), rng = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / rng) * h}`).join(" ");
  const rising = data[data.length - 1] >= data[0];
  const color = rising ? "#34d399" : "#f87171";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" data-testid="revenue-sparkline">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Relative date bucket for grouping activity: Today / Yesterday / weekday / date.
const dateBucket = (iso) => {
  if (!iso) return "Earlier";
  const d = new Date(iso), now = new Date();
  const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.floor((sod - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString();
};

const FAV_SECTIONS = { clients: "Clients", projects: "Projects", proposals: "Proposals", ai: "AI Center", templates: "Templates", requests: "Assessment Requests", orders: "Orders", payments: "Payments", invoices: "Invoices", messages: "Messages", reports: "Reports" };

export function Overview({ onNav }) {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [reports, setReports] = useState(null);
  const [today, setToday] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [inq, setInq] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem("admin_fav_actions") || "[]"); } catch (e) { return []; } });
  const [favPicker, setFavPicker] = useState(false);
  const saveFavs = (arr) => { setFavs(arr); try { localStorage.setItem("admin_fav_actions", JSON.stringify(arr)); } catch (e) {} };
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/admin/assistant-funnel").then((r) => setFunnel(r.data)).catch(() => {});
    api.get("/admin/inquiries/counts").then((r) => setInq(r.data)).catch(() => {});
  }, []);
  if (!stats) return <Loader />;

  const t = today || null;
  const rev = t?.revenue;
  const openPipelineStage = (stage) => { try { sessionStorage.setItem("admin_focus_stage", stage); } catch (e) {} onNav("projects"); };

  const widgets = [
    { key: "revenue", label: "Today's Revenue", value: money(rev ? rev.today : 0), sub: t ? `${money(rev.month)} this month` : "—", icon: DollarSign, go: "payments", pct: rev ? rev.today_pct : null, pctSuffix: " vs yesterday" },
    { key: "active", label: "Active Projects", value: stats.active_projects, sub: `${stats.projects} total`, icon: FolderKanban, go: "projects" },
    { key: "proposals", label: "Pending Proposals", value: stats.pending_proposals, sub: `${stats.proposals} total`, icon: FileText, go: "proposals" },
    { key: "sales", label: "Website Sales", value: stats.website_sales, sub: money(stats.website_sales_revenue), icon: ShoppingBag, go: "orders" },
    { key: "leads", label: "New Assessments", value: stats.new_leads, sub: "last 30 days", icon: UserPlus, go: "requests" },
    { key: "inquiries", label: "Build Inquiries", value: inq?.new ?? 0, sub: inq?.stale ? `${inq.stale} need follow-up` : "new custom-build leads", icon: Target, go: "inquiries" },
    { key: "ai", label: "AI Generations", value: stats.ai_usage, sub: t ? `${t.ai.sessions_today} today` : "generations", icon: Sparkles, go: "aihistory" },
    { key: "stripe", label: "Stripe Payments", value: stats.stripe_payments, sub: "paid sessions", icon: CreditCard, go: "payments" },
    { key: "messages", label: "Client Messages", value: stats.client_messages, sub: `${stats.clients} clients`, icon: MessageSquare, go: "messages" },
  ];

  const revData = reports ? Object.entries(reports.revenue_by_month).sort().map(([m, v]) => ({ month: m.slice(2), revenue: Math.round(v) })) : [];
  const pipeline = STAGES.map((s) => ({ stage: s.split(" ")[0], full: s, count: stats.stage_counts[s] || 0 }));

  const focus = (t ? [
    { key: "overdue", n: t.focus.invoices_overdue, text: `invoice${t.focus.invoices_overdue === 1 ? "" : "s"} overdue`, icon: AlertTriangle, sev: "red", go: "invoices" },
    { key: "failed", n: t.focus.payments_failed, text: `Stripe payment${t.focus.payments_failed === 1 ? "" : "s"} failed`, icon: CreditCard, sev: "red", go: "payments" },
    { key: "proposals", n: t.focus.proposals_awaiting, text: `proposal${t.focus.proposals_awaiting === 1 ? "" : "s"} awaiting review`, icon: FileText, sev: "amber", go: "proposals" },
    { key: "leads", n: t.focus.new_leads_week, text: `new assessment${t.focus.new_leads_week === 1 ? "" : "s"} this week`, icon: UserPlus, sev: "amber", go: "requests" },
    { key: "inquiries", n: inq?.stale || 0, text: `custom-build inquir${(inq?.stale || 0) === 1 ? "y" : "ies"} awaiting follow-up`, icon: Target, sev: "amber", go: "inquiries" },
    { key: "launches", n: t.trends.projects_due_today, text: `launch${t.trends.projects_due_today === 1 ? "" : "es"} today`, icon: FolderKanban, sev: "green", go: "projects" },
    { key: "ai", n: t.focus.ai_today, text: `AI conversation${t.focus.ai_today === 1 ? "" : "s"} today`, icon: Bot, sev: "green", go: "ai" },
  ].filter((f) => f.n > 0) : []).sort((a, b) => ({ red: 0, amber: 1, green: 2 }[a.sev] - { red: 0, amber: 1, green: 2 }[b.sev]));

  const aiMetrics = t ? [
    { label: "Sessions Today", value: t.ai.sessions_today, icon: Zap },
    { label: "Proposals Generated", value: t.ai.proposals_generated, icon: FileText },
    { label: "Hours Saved", value: `${t.ai.hours_saved}h`, icon: Timer },
    { label: "Tokens Used", value: t.ai.tokens_used.toLocaleString(), icon: Bot },
    { label: t.ai.cost_basis === "actual" ? "Actual Cost" : "Estimated Cost", value: money(t.ai.est_cost), icon: Coins },
    { label: "Avg Cost / Session", value: money(t.ai.avg_cost_per_session), icon: Coins },
    { label: "Most Used Tool", value: t.ai.most_used_tool, icon: Wrench },
    { label: "Top Strategist", value: t.ai.top_strategist, icon: Sparkles },
  ] : [];

  const summary = t ? [
    { label: "Revenue", value: money(t.daily_summary.revenue) },
    { label: "Website Sales", value: t.daily_summary.website_sales },
    { label: "Templates Sold", value: t.daily_summary.templates_sold },
    { label: "New Assessments", value: t.daily_summary.new_leads },
    { label: "Projects Completed", value: t.daily_summary.projects_completed },
    { label: "AI Hours Saved", value: `${t.daily_summary.ai_hours_saved}h` },
    { label: "New Reviews", value: t.daily_summary.new_reviews },
    { label: "Stripe Payments", value: t.daily_summary.stripe_payments },
  ] : [];

  const health = t?.health;

  return (
    <div>
      {/* Business Alerts */}
      {t && (
        <div className="mb-6" data-testid="business-alerts">
          <div className="flex items-center gap-2 mb-2 text-gray-400 text-xs font-medium uppercase tracking-wider"><ShieldCheck size={14} className="text-gold" /> Business Alerts</div>
          {t.alerts.length === 0 ? (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 px-4 py-3 text-emerald-300 text-sm" data-testid="alerts-clear">
              <CheckCircle2 size={16} /> Everything is operating normally. No business alerts require your attention.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {t.alerts.map((a, i) => {
                const s = SEV[a.severity] || SEV.blue;
                const Icon = s.icon;
                return (
                  <button key={i} onClick={() => onNav(a.go)} data-testid={`alert-${i}`}
                    className={`flex items-center gap-3 text-left bg-navy-800 border ${s.ring} px-4 py-3 hover:bg-navy-700 transition-colors group`}>
                    <Icon size={16} className={s.text.replace("text-", "text-")} />
                    <span className={`text-sm ${s.text} flex-1`}>{a.text}</span>
                    <ChevronRight size={15} className="text-gray-600 group-hover:text-gold transition-colors" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Executive morning briefing */}
      {t && (
        <div className="bg-gradient-to-r from-navy-800 to-navy-900 border border-white/5 p-6 mb-6" data-testid="dashboard-greeting">
          <div className="flex items-center gap-2 text-gold mb-1"><Sun size={18} /><span className="text-sm font-medium tracking-wide">{t.greeting}, {t.name}.</span></div>
          <h2 className="font-heading text-2xl text-white mb-3">Today's Business Summary</h2>
          {t.briefing.length > 0 ? (
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="briefing">
              {t.briefing.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-200 text-sm"><span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> {b}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400 text-sm">A fresh start — no key metrics to report yet today. Everything operating normally.</p>
          )}
        </div>
      )}

      {/* Quick actions + favorites */}
      <div className="flex flex-wrap items-center gap-2.5 mb-6" data-testid="quick-actions">
        {QUICK_ACTIONS.map((a) => (
          <button key={a.label} onClick={() => onNav(a.go)} data-testid={`quick-action-${a.go}`}
            className="flex items-center gap-2 bg-navy-800 border border-white/10 hover:border-gold/50 hover:bg-navy-700 text-gray-200 hover:text-white px-4 py-2.5 text-sm font-medium transition-colors">
            <Plus size={14} className="text-gold" /> {a.label}
          </button>
        ))}
        {favs.filter((k) => FAV_SECTIONS[k]).map((k) => (
          <span key={k} data-testid={`fav-${k}`} className="flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold px-4 py-2.5 text-sm font-medium">
            <button onClick={() => onNav(k)} className="flex items-center gap-2"><Star size={13} className="fill-gold" /> {FAV_SECTIONS[k]}</button>
            <button onClick={() => saveFavs(favs.filter((x) => x !== k))} data-testid={`fav-remove-${k}`} aria-label="Remove favorite" className="text-gold/60 hover:text-gold"><X size={13} /></button>
          </span>
        ))}
        <div className="relative">
          <button onClick={() => setFavPicker((o) => !o)} data-testid="fav-add"
            className="flex items-center gap-2 border border-dashed border-white/20 hover:border-gold/50 text-gray-400 hover:text-gold px-4 py-2.5 text-sm font-medium transition-colors">
            <Star size={14} /> Favorite
          </button>
          {favPicker && (
            <div className="absolute z-40 mt-2 w-48 bg-navy-900 border border-white/10 shadow-2xl py-1" data-testid="fav-picker">
              {Object.entries(FAV_SECTIONS).filter(([k]) => !favs.includes(k)).map(([k, label]) => (
                <button key={k} onClick={() => { saveFavs([...favs, k]); setFavPicker(false); }} data-testid={`fav-pick-${k}`}
                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white flex items-center gap-2"><Plus size={13} className="text-gold" /> {label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Head title="Business Dashboard" desc="Your entire operation at a glance." />

      {/* Widget grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {widgets.map((w) => (
          <button key={w.key} onClick={() => onNav(w.go)} data-testid={`widget-${w.key}`}
            className="text-left bg-navy-800 border border-white/5 p-6 hover:border-gold/40 transition-colors group">
            <div className="flex items-center justify-between mb-4">
              <w.icon className="text-gold" size={20} />
              <ChevronRight className="text-gray-600 group-hover:text-gold transition-colors" size={16} />
            </div>
            <div className="font-heading text-3xl text-white">{w.value}</div>
            <div className="text-gray-400 text-sm mt-1">{w.label}</div>
            {typeof w.pct === "number" ? (
              <div className="mt-1.5" data-testid={`trend-${w.key}`}><Trend pct={w.pct} suffix={w.pctSuffix} /></div>
            ) : (
              <div className="text-gray-600 text-xs mt-0.5">{w.sub}</div>
            )}
          </button>
        ))}
      </div>

      {/* Revenue detail + Business Health */}
      {t && (
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 bg-navy-800 border border-white/5 p-6" data-testid="revenue-card">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-white flex items-center gap-2"><DollarSign size={18} className="text-gold" /> Revenue</h3>
              <div className="flex items-center gap-4">
                {rev.spark && <div className="hidden sm:block" title="Revenue — last 14 days"><Sparkline data={rev.spark} /></div>}
                <button onClick={() => onNav("reports")} className="text-xs text-gray-400 hover:text-gold transition-colors flex items-center gap-1">View Reports <ChevronRight size={13} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "Today", value: money(rev.today), extra: <Trend pct={rev.today_pct} suffix=" vs yest." /> },
                { label: "Yesterday", value: money(rev.yesterday) },
                { label: "This Month", value: money(rev.month), extra: <Trend pct={rev.delta_month_pct} suffix=" vs last mo" /> },
                { label: "This Quarter", value: money(rev.quarter) },
                { label: "Year to Date", value: money(rev.ytd) },
              ].map((r) => (
                <div key={r.label} className="bg-navy-900/60 border border-white/5 p-4" data-testid={`revenue-${r.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                  <div className="text-gray-500 text-xs mb-1.5">{r.label}</div>
                  <div className="font-heading text-2xl text-white leading-none">{r.value}</div>
                  {r.extra && <div className="mt-1.5">{r.extra}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Business Health */}
          {health && (
            <div className="bg-navy-800 border border-white/5 p-6" data-testid="business-health">
              <h3 className="font-heading text-xl text-white flex items-center gap-2 mb-4"><HeartPulse size={18} className="text-gold" /> Business Health</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className={`relative w-20 h-20 rounded-full flex items-center justify-center border-4 ${health.color === "green" ? "border-emerald-500" : health.color === "amber" ? "border-amber-400" : health.color === "gray" ? "border-gray-600" : "border-red-500"}`}>
                  <span className="font-heading text-2xl text-white" data-testid="health-score">{health.score ?? "—"}</span>
                </div>
                <div>
                  {typeof health.delta_month === "number" && (
                    <div className="mb-1" data-testid="health-trend">
                      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${health.delta_month > 0 ? "text-emerald-400" : health.delta_month < 0 ? "text-red-400" : "text-gray-400"}`}>
                        {health.delta_month > 0 ? <ArrowUp size={12} /> : health.delta_month < 0 ? <ArrowDown size={12} /> : <ArrowRight size={12} />}
                        {health.delta_month >= 0 ? "+" : ""}{health.delta_month} this month
                      </span>
                    </div>
                  )}
                  <div className="flex gap-0.5 mb-1" data-testid="health-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={14} className={n <= Math.round(health.stars || 0) ? "text-gold fill-gold" : "text-gray-600"} />
                    ))}
                  </div>
                  <div className={`text-xs ${health.color === "green" ? "text-emerald-400" : health.color === "amber" ? "text-amber-300" : health.color === "gray" ? "text-gray-400" : "text-red-400"}`}>{health.summary}</div>
                </div>
              </div>
              <div className="space-y-2">
                {health.factors.map((f) => (
                  <div key={f.label} className="flex items-center gap-2" data-testid={`health-factor-${f.label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <span className="text-gray-500 text-[11px] w-28 shrink-0 truncate">{f.label}</span>
                    <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden">
                      {f.has_data && f.score !== null && (
                        <div className={`h-full ${f.score >= 75 ? "bg-emerald-500" : f.score >= 50 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${f.score}%` }} />
                      )}
                    </div>
                    {f.has_data && f.score !== null
                      ? <span className="text-gray-400 text-[11px] w-7 text-right">{f.score}</span>
                      : <span className="text-gray-600 text-[11px] w-7 text-right" title="No data yet">N/A</span>}
                  </div>
                ))}
              </div>
              {typeof health.factors_with_data === "number" && health.factors_with_data < health.factors_total && (
                <div className="text-gray-600 text-[11px] mt-3" data-testid="health-data-caption">
                  Score based on {health.factors_with_data} of {health.factors_total} signals with data. Others show N/A until activity comes in.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Usage */}
      {t && (
        <div className="bg-navy-800 border border-white/5 p-6 mb-6" data-testid="ai-usage-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl text-white flex items-center gap-2"><Sparkles size={18} className="text-gold" /> AI Usage</h3>
            <button onClick={() => onNav("ai")} data-testid="ai-view-center" className="text-xs text-gray-400 hover:text-gold transition-colors flex items-center gap-1">View AI Center <ChevronRight size={13} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aiMetrics.map((m) => (
              <div key={m.label} className="bg-navy-900/60 border border-white/5 p-4" data-testid={`ai-metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <m.icon size={16} className="text-gold mb-2" />
                <div className="font-heading text-xl text-white leading-none truncate">{m.value}</div>
                <div className="text-gray-500 text-xs mt-1.5">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Conversion Funnel */}
      {funnel && (
        <div className="bg-navy-800 border border-white/5 p-6 mb-6" data-testid="ai-funnel-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl text-white flex items-center gap-2"><TrendingUp size={18} className="text-gold" /> AI Conversion Funnel</h3>
            <span className="flex items-center gap-1.5 text-xs text-gray-400" data-testid="funnel-conversion-rate">
              Conversion <b className="text-gold text-sm">{funnel.totals.conversion_rate}%</b>
            </span>
          </div>
          {/* Funnel stages */}
          <div className="flex flex-wrap items-stretch gap-2 mb-6">
            {funnel.funnel.map((f, i) => {
              const Ico = FUNNEL_ICON[f.key] || Bot;
              const max = Math.max(funnel.funnel[0].value, 1);
              const pct = Math.max(6, Math.round((f.value / max) * 100));
              return (
                <div key={f.key} className="flex items-stretch gap-2 flex-1 min-w-[150px]">
                  <button onClick={() => onNav(f.go)} data-testid={`funnel-${f.key}`}
                    className="flex-1 text-left bg-navy-900/60 border border-white/5 hover:border-gold/40 transition-colors p-4 group">
                    <div className="flex items-center justify-between mb-2">
                      <Ico size={16} className="text-gold" />
                      <ChevronRight size={14} className="text-gray-700 group-hover:text-gold transition-colors" />
                    </div>
                    <div className="font-heading text-2xl text-white leading-none">{f.value}</div>
                    <div className="text-gray-500 text-[11px] mt-1.5 leading-tight">{f.label}</div>
                    <div className="mt-2 h-1 bg-white/5 rounded overflow-hidden">
                      <div className="h-full bg-gold/70" style={{ width: `${pct}%` }} />
                    </div>
                  </button>
                  {i < funnel.funnel.length - 1 && (
                    <div className="hidden lg:flex items-center text-gray-700"><ArrowRight size={16} /></div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Intent breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "ready_for_project", label: "Ready for Project", icon: FolderKanban },
              { key: "proposal_requests", label: "Proposal Requests", icon: FileText },
              { key: "content_requests", label: "Content Requests", icon: Bot },
              { key: "general_questions", label: "General Questions", icon: MessageSquare },
            ].map((m) => (
              <div key={m.key} className="bg-navy-900/60 border border-white/5 p-4" data-testid={`funnel-intent-${m.key}`}>
                <m.icon size={16} className="text-gold mb-2" />
                <div className="font-heading text-xl text-white leading-none">{funnel.intents[m.key]}</div>
                <div className="text-gray-500 text-xs mt-1.5">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Focus + Recent Activity */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-navy-800 border border-white/5 p-6" data-testid="todays-focus">
          <h3 className="font-heading text-xl text-white flex items-center gap-2 mb-5"><Sun size={18} className="text-gold" /> Today's Focus</h3>
          {focus.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-8" data-testid="focus-empty">
              <span className="text-3xl mb-2">🎉</span>
              <p className="text-white text-sm font-medium">You're all caught up.</p>
              <p className="text-gray-500 text-sm mt-1">Nothing needs your attention right now.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {focus.map((f) => (
                <button key={f.key} onClick={() => onNav(f.go)} data-testid={`focus-${f.key}`}
                  className="w-full flex items-center gap-3 text-left px-3 py-2.5 -mx-1 rounded hover:bg-white/5 transition-colors group">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${SEV[f.sev].dot}`} data-testid={`focus-dot-${f.key}`} />
                  <span className={`shrink-0 flex items-center justify-center w-7 h-7 rounded ${SEV[f.sev].tile}`}><f.icon size={14} /></span>
                  <span className="text-sm text-gray-200"><span className="font-semibold text-white">{f.n}</span> {f.text}</span>
                  <ChevronRight size={15} className="ml-auto text-gray-600 group-hover:text-gold transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-navy-800 border border-white/5 p-6" data-testid="recent-activity">
          <h3 className="font-heading text-xl text-white flex items-center gap-2 mb-5"><Activity size={18} className="text-gold" /> Recent Activity</h3>
          <div className="space-y-1 max-h-[320px] overflow-y-auto pr-1">
            {activity.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-8" data-testid="activity-empty">
                <span className="text-2xl mb-2">📋</span>
                <p className="text-gray-400 text-sm">No activity yet — it'll appear here as things happen.</p>
              </div>
            ) : (() => {
              const groups = [];
              activity.forEach((a) => {
                const b = dateBucket(a.created_at);
                let g = groups.find((x) => x.bucket === b);
                if (!g) { g = { bucket: b, items: [] }; groups.push(g); }
                g.items.push(a);
              });
              let idx = -1;
              return groups.map((g) => (
                <div key={g.bucket} data-testid={`activity-group-${g.bucket.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider px-2 pt-3 pb-1">{g.bucket}</div>
                  {g.items.map((a) => {
                    idx += 1;
                    const i = idx, m = actMeta(a.type);
                    return (
                      <button key={i} onClick={() => onNav(m.go)} data-testid={`activity-${i}`}
                        className="w-full flex gap-3 text-left px-2 py-2 -mx-1 rounded hover:bg-white/5 transition-colors group items-start">
                        <span className={`mt-0.5 shrink-0 flex items-center justify-center w-7 h-7 rounded ${m.cls}`}><m.icon size={14} /></span>
                        <div className="min-w-0 flex-1">
                          <div className="text-gray-200 text-sm leading-snug group-hover:text-white transition-colors">{a.text}</div>
                          <div className="text-gray-600 text-xs mt-0.5">{timeAgo(a.created_at)}</div>
                        </div>
                        <ChevronRight size={15} className="mt-1.5 shrink-0 text-gray-700 group-hover:text-gold transition-colors" />
                      </button>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Daily Business Summary (collapsible) */}
      {t && (
        <div className="bg-navy-800 border border-white/5 mb-6" data-testid="daily-summary">
          <button onClick={() => setSummaryOpen((o) => !o)} data-testid="daily-summary-toggle"
            className="w-full flex items-center justify-between px-6 py-4 text-left">
            <h3 className="font-heading text-xl text-white flex items-center gap-2"><Layers size={18} className="text-gold" /> Today's Summary</h3>
            <ChevronDown size={18} className={`text-gray-400 transition-transform ${summaryOpen ? "rotate-180" : ""}`} />
          </button>
          {summaryOpen && (
            <div className="px-6 pb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {summary.map((s) => (
                  <div key={s.label} className="bg-navy-900/60 border border-white/5 p-4">
                    <div className="font-heading text-2xl text-white leading-none">{s.value}</div>
                    <div className="text-gray-500 text-xs mt-1.5">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle2 size={15} /> {t.alerts.some((a) => a.severity === "red") ? "Some items need your attention above." : "Everything is operating normally."}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analytics + Pipeline */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-navy-800 border border-white/5 p-6" data-testid="website-analytics">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => onNav("reports")} data-testid="analytics-open-reports" className="font-heading text-xl text-white flex items-center gap-2 hover:text-gold transition-colors"><TrendingUp size={18} className="text-gold" /> Website Analytics <ChevronRight size={15} className="text-gray-600" /></button>
            <div className="flex gap-6 text-sm">
              <button onClick={() => onNav("requests")} title="Total assessment requests captured from the website" className="hover:text-gold transition-colors"><span className="text-gray-500">Assessments</span> <span className="text-white font-semibold ml-1">{stats.requests}</span></button>
              <button onClick={() => onNav("clients")} title="Newsletter subscribers" className="hover:text-gold transition-colors"><span className="text-gray-500">Subscribers</span> <span className="text-white font-semibold ml-1">{stats.subscribers}</span></button>
            </div>
          </div>
          {revData.length > 0 ? (
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={revData} margin={{ left: -18 }} onClick={() => onNav("reports")} style={{ cursor: "pointer" }}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="95%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
                <Tooltip contentStyle={{ background: "#172033", border: "1px solid #ffffff20", color: "#fff" }} formatter={(v) => [money(v), "Revenue"]} labelFormatter={(l) => `Month ${l} — click to open Reports`} />
                <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-[230px] flex items-center justify-center text-gray-600 text-sm">Revenue trends appear here as payments come in.</div>}
        </div>

        <div className="bg-navy-800 border border-white/5 p-6" data-testid="project-pipeline">
          <h3 className="font-heading text-xl text-white mb-1">Project pipeline</h3>
          <p className="text-gray-600 text-xs mb-5">Click a stage to view its projects.</p>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={pipeline}>
              <XAxis dataKey="stage" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
              <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
              <Tooltip contentStyle={{ background: "#172033", border: "1px solid #ffffff20", color: "#fff" }} cursor={{ fill: "#ffffff08" }} formatter={(v) => [`${v} projects`, "Count"]} labelFormatter={(l) => `${l} — click to filter`} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => d && openPipelineStage(d.full)}>
                {pipeline.map((p, i) => <Cell key={i} fill="#D4AF37" data-testid={`pipeline-bar-${p.full.replace(/\s+/g, "-").toLowerCase()}`} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
